import {
  Box,
  Chip,
  FormControlLabel,
  Grid2 as Grid,
  Switch,
  TextField,
  Typography,
  Link,
} from "@mui/material";
import { FC } from "react";
import { Controller, FieldErrors, useFormContext } from "react-hook-form";
import { useGetPartnerDropdownsQuery } from "src/shared/api/dropdowns/partner";
import { SelectField, ContentAccordion } from "src/shared/components";
import { EPartnerPriority } from "src/shared/types/partner.types";
import { messages } from "src/shared/utils/hookFormRules";
import { IPartnerFormValues } from "../../model/types";
import {
  IEditPartnerFormValues,
  IPartnerBaseFormValues,
} from "../../model/types/partner";

export const ClientDescription: FC = () => {
  const { data: dropdowns } = useGetPartnerDropdownsQuery();
  const { partner_statuses, partner_priority } = dropdowns || {};
  const {
    control,
    formState: { errors },
    watch,
  } = useFormContext<IPartnerFormValues | IEditPartnerFormValues>();

  const validatingFields: Array<keyof FieldErrors<IPartnerBaseFormValues>> = [
    "priority",
    "status",
  ];
  const hasErrors =
    errors.partner &&
    validatingFields.some((field) => errors.partner?.[field] !== undefined);
  const isNdaSigned = watch("partner.is_nda_signed");
  const ndaFile = watch("partner.nda_file");
  const currentNdaFile = typeof ndaFile === "string" ? ndaFile : null;

  return (
    <ContentAccordion
      title={
        <Box display="flex" alignItems="center">
          <Chip label="4" />
          <Typography
            color={hasErrors ? "error" : "grey.500"}
            sx={{ ml: 2 }}
            component="span"
          >
            Описание клиента
          </Typography>
        </Box>
      }
    >
      <Grid container spacing={4}>
        <Grid size={{ xs: 12, sm: 6, lg: 2 }}>
          <Controller
            control={control}
            name="partner.status"
            render={({ field: { ref, ...field }, fieldState: { error } }) => (
              <SelectField
                inputRef={ref}
                fullWidth
                {...field}
                label="Статус клиента"
                error={!!error}
                helperText={error?.message}
                options={partner_statuses}
              />
            )}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 2 }}>
          <Controller
            control={control}
            name="partner.priority"
            render={({ field: { ref, ...field }, fieldState: { error } }) => (
              <SelectField<EPartnerPriority>
                inputRef={ref}
                fullWidth
                {...field}
                label="Категория"
                error={!!error}
                helperText={error?.message}
                options={partner_priority}
              />
            )}
            rules={{ required: messages.required }}
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 6, lg: 2 }}>
          <Controller
            name="partner.is_nda_signed"
            render={({ field }) => (
              <FormControlLabel
                control={<Switch {...field} checked={!!field.value} />}
                label="Подписан NDA"
              />
            )}
          />
        </Grid>
        {isNdaSigned && (
          <>
            <Grid size={12}>
              {currentNdaFile && (
                <Box mb={2}>
                  <Typography variant="body2">Текущий файл:</Typography>
                  <Link href={currentNdaFile} target="_blank" rel="noopener">
                    Просмотреть файл
                  </Link>
                </Box>
              )}
            </Grid>
            <Grid size={12}>
              <Controller
                name="partner.nda_file"
                control={control}
                rules={{
                  validate: (file) => {
                    if (typeof file === "string") return true;

                    if (!file && !currentNdaFile) {
                      return messages.file;
                    }

                    if (file) {
                      const allowedTypes = [
                        "application/pdf",
                        "image/jpeg",
                        "image/png",
                      ];
                      if (!allowedTypes.includes(file.type)) {
                        return "Допустимые форматы: PDF, JPEG, PNG";
                      }
                      if (file.size > 2_000_000) {
                        return "Максимальный размер: 2MB";
                      }
                    }

                    return true;
                  },
                }}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    type="file"
                    inputRef={field.ref}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      field.onChange(
                        (e.target as HTMLInputElement).files?.[0] || null,
                      );
                    }}
                    error={!!error}
                    helperText={error?.message}
                    InputLabelProps={{
                      shrink: true,
                      children: currentNdaFile
                        ? "Заменить файл (PDF, JPEG или PNG)"
                        : "Прикрепите файл (PDF, JPEG или PNG)",
                    }}
                    inputProps={{
                      accept: "application/pdf, image/jpeg, image/png",
                    }}
                  />
                )}
              />
            </Grid>
            <Grid size={12}>
              <Controller
                name="partner.nda_note"
                rules={{
                  required: isNdaSigned && messages.required,
                  maxLength: {
                    value: 256,
                    message: "Максимум 256 символов",
                  },
                }}
                render={({
                  field: { ref, ...field },
                  fieldState: { error },
                }) => (
                  <Box
                    width={(theme) => ({
                      xs: "100%",
                      sm: `calc(50% - ${theme.spacing(2)})`,
                      lg: `calc(25% - ${theme.spacing(2)})`,
                    })}
                  >
                    <TextField
                      inputRef={ref}
                      fullWidth
                      multiline
                      rows={3}
                      {...field}
                      label="Комментарий по NDA"
                      error={!!error}
                      helperText={error?.message}
                      slotProps={{
                        htmlInput: {
                          maxLength: 257,
                        },
                      }}
                    />
                  </Box>
                )}
              />
            </Grid>
          </>
        )}
      </Grid>
    </ContentAccordion>
  );
};
